import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../config/firebaseWithFailover';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  getNotificationState, 
  updatePageVisit, 
  updateLoadVisit,
  isNewerThanPageVisit,
  isNewerThanLoadVisit,
  getNotificationCategory,
  isLoadSpecificNotification,
  getLoadIdFromNotification,
  markNotificationsAsRead,
  markLoadNotificationsAsRead,
  cleanupReadNotifications,
  NotificationState,
  Notification
} from '../services/notificationTrackingService';
import Logger from '../services/logger';

interface NotificationCounts {
  myLoads: number;
  availableLoads: number;
  partners: number;
  payments: number;
  bellOnly: number;
  total: number;
  loadSpecific: { [loadId: string]: number };
}

export const useEnhancedCarrierNotifications = () => {
  const [counts, setCounts] = useState<NotificationCounts>({
    myLoads: 0,
    availableLoads: 0,
    partners: 0,
    payments: 0,
    bellOnly: 0,
    total: 0,
    loadSpecific: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notificationState, setNotificationState] = useState<NotificationState | null>(null);

  // Load notification state when user changes
  useEffect(() => {
    const auth = getFailoverAwareAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          setLoading(true);
          setError(null);
          
          // Load user's notification state
          const state = await getNotificationState(user.uid);
          setNotificationState(state);
          
          // Clean up old read notifications (run in background)
          cleanupReadNotifications(user.uid).catch(err => 
            Logger.warn('Failed to cleanup old notifications:', { err })
          );
          
        } catch (err) {
          Logger.error('Error loading notification state:', err instanceof Error ? err : new Error(String(err)), { err });
          setError('Failed to load notification state');
          setLoading(false);
        }
      } else {
        setNotificationState(null);
        setCounts({
          myLoads: 0,
          availableLoads: 0,
          partners: 0,
          payments: 0,
          bellOnly: 0,
          total: 0,
          loadSpecific: {}
        });
        setLoading(false);
        setError(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Listen to notifications when user and state are available
  useEffect(() => {
    const auth = getFailoverAwareAuth();
    if (!auth.currentUser || !notificationState) return;

    const db = getFailoverAwareDatabase();
    // FIXED: Add limit and orderBy for scalability (Phase 4)
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('recipientId', '==', auth.currentUser.uid),
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(100) // Limit to 100 most recent unread notifications
    );

    const unsubscribeNotifications = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notifications: Notification[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          notifications.push({
            id: doc.id,
            type: data.type || 'unknown',
            read: data.read || false,
            createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
            loadId: data.loadId,
            poNumber: data.poNumber,
            ...data
          });
        });

        // Count notifications by category with enhanced logic
        const newCounts: NotificationCounts = {
          myLoads: 0,
          availableLoads: 0,
          partners: 0,
          payments: 0,
          bellOnly: 0,
          total: notifications.length,
          loadSpecific: {}
        };

        // Bell-only notifications (informational only)
        const BELL_ONLY_TYPES = [
          'offer_rejected',
          'carrier_decline',
          'carrier_reject',
          'system_alert',
          'rating_received',
          'message_received',
          'carrier_offline',
          'marketplace'
        ];

        notifications.forEach(notification => {
          const { type } = notification;
          
          // Check if it's a bell-only notification
          if (BELL_ONLY_TYPES.includes(type)) {
            newCounts.bellOnly++;
            return;
          }

          // Get category for sidebar dot notifications
          const category = getNotificationCategory(type);
          if (!category) return;

          // Check if notification is newer than last page visit
          const lastPageVisit = notificationState.pageVisits[category as keyof NotificationState['pageVisits']];
          const isNewForPage = isNewerThanPageVisit(notification, lastPageVisit);

          if (isNewForPage) {
            // Count for page category
            if (category in newCounts) {
              (newCounts as any)[category]++;
            }

            // Count for load-specific notifications
            if (isLoadSpecificNotification(notification)) {
              const loadId = getLoadIdFromNotification(notification);
              if (loadId) {
                const lastLoadVisit = notificationState.loadVisits[loadId];
                const isNewForLoad = isNewerThanLoadVisit(notification, lastLoadVisit);
                
                if (isNewForLoad) {
                  newCounts.loadSpecific[loadId] = (newCounts.loadSpecific[loadId] || 0) + 1;
                }
              }
            }
          }
        });

        setCounts(newCounts);
        setLoading(false);
      },
      (error) => {
        Logger.error('Error fetching notifications:', error instanceof Error ? error : new Error(String(error)), { error });
        setError('Failed to load notifications');
        setLoading(false);
      }
    );

    return () => unsubscribeNotifications();
  }, [notificationState]);

  // Function to mark page as visited
  const markPageVisited = async (pageType: keyof NotificationState['pageVisits']) => {
    const auth = getFailoverAwareAuth();
    if (!auth.currentUser) return;
    
    try {
      const now = new Date();
      
      // Optimistically update local state first for immediate UI feedback
      setNotificationState(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          pageVisits: {
            ...prev.pageVisits,
            [pageType]: now
          }
        };
      });
      
      // Update page visit timestamp in Firestore
      await updatePageVisit(auth.currentUser.uid, pageType);
      
      // Mark notifications as read for this page
      // This will cause them to disappear from the query (read == false)
      // which will trigger onSnapshot and recalculate counts automatically
      await markNotificationsAsRead(auth.currentUser.uid, pageType);
      
      // Note: Counts will automatically recalculate when:
      // 1. Notifications are marked as read and disappear from the query
      // 2. onSnapshot fires with updated notification list
      // 3. The useEffect recalculates counts based on updated notificationState
    } catch (error) {
      Logger.error(`Error marking ${pageType} as visited:`, error instanceof Error ? error : new Error(String(error)), { error, pageType });
      // Revert optimistic update on error
      setNotificationState(prev => {
        if (!prev) return prev;
        // Reload state from Firestore on error
        getNotificationState(auth.currentUser!.uid).then(state => {
          setNotificationState(state);
        }).catch(err => {
          Logger.error('Error reloading notification state:', err instanceof Error ? err : new Error(String(err)), { err });
        });
        return prev;
      });
    }
  };

  // Function to mark load as visited
  const markLoadVisited = async (loadId: string) => {
    const auth = getFailoverAwareAuth();
    if (!auth.currentUser) return;
    
    try {
      // Update load visit timestamp
      await updateLoadVisit(auth.currentUser.uid, loadId);
      
      // Mark load-specific notifications as read
      await markLoadNotificationsAsRead(auth.currentUser.uid, loadId);
      
      // Update local state
      setNotificationState(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          loadVisits: {
            ...prev.loadVisits,
            [loadId]: new Date()
          }
        };
      });
    } catch (error) {
      Logger.error(`Error marking load ${loadId} as visited:`, error instanceof Error ? error : new Error(String(error)), { error, loadId });
    }
  };

  return {
    counts,
    loading,
    error,
    hasAnyNotifications: counts.total > 0,
    hasSidebarNotifications: counts.myLoads > 0 || counts.availableLoads > 0 || counts.partners > 0 || counts.payments > 0,
    hasBellNotifications: counts.bellOnly > 0,
    markPageVisited,
    markLoadVisited,
    getLoadNotificationCount: (loadId: string) => counts.loadSpecific[loadId] || 0
  };
};

export default useEnhancedCarrierNotifications;
